"use client";

import { Check, Copy, Sparkles } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/hooks/useLanguage";
import { useUserInfo } from "@/hooks/useUserInfo";
import { getSpecialAddressInfo } from "@/lib/utils/address-display";
import { cn } from "@/lib/utils";
import { RankingItem } from "./ranking-item";
import { FOUNDATION_PRINCIPAL, type SponsoredHolding } from "./types";
import {
	RANK_META,
	buildTokenImageUrl,
	formatHoldingBalanceCompact,
	formatHoldingBalanceFull,
	formatHoldingUsdValue,
	formatHoldingUsdValueFull,
	getSupportAgeToneClass,
	formatSupportAgeLabel,
	formatTokenPrice,
} from "./utils";

const FOUNDATION_PRESET = "foundation";
const SPONSORED_COPY_QQ = "1131202977";

interface PromoDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	holdings: SponsoredHolding[];
	btcPrice: number;
}

interface MobileRankingItemProps {
	holding: SponsoredHolding;
	rank: number;
	holdingsLabel: string;
	valuationLabel: string;
	btcPrice: number;
}

interface FoundationAccountCardProps {
	copyLabel: string;
	idLabel: string;
	language: "zh" | "en";
	onCopyPrincipal: () => Promise<void>;
	isCopied: boolean;
}

function MobileRankingItem({
	holding,
	rank,
	holdingsLabel,
	valuationLabel,
	btcPrice,
}: MobileRankingItemProps) {
	const medal = RANK_META[Math.min(rank, RANK_META.length - 1)] ?? RANK_META[2];
	const { language } = useLanguage();

		return (
			<div className="relative w-full overflow-hidden rounded-[22px] border border-light-border/80 bg-light-background/85 p-3 dark:border-dark-border/80 dark:bg-dark-background/70">
				<div
					className="absolute inset-y-3 left-0 w-[3px] rounded-full"
					style={{
						background: `linear-gradient(180deg, ${medal.colors[0]} 0%, ${medal.colors[1]} 100%)`,
						opacity: 0.48,
					}}
				/>
				<div className="flex items-start gap-3 pl-2">
					<div className="relative h-10 w-10 shrink-0">
						<div className="relative h-10 w-10 overflow-hidden rounded-full border border-white/60 bg-light-surface shadow-[0_8px_18px_rgba(15,23,42,0.08)] dark:bg-dark-surface dark:shadow-[0_10px_22px_rgba(0,0,0,0.28)]">
							<Image
								src={buildTokenImageUrl(holding.token.id)}
								alt={holding.token.name}
								fill
								sizes="40px"
								className="object-cover"
							/>
						</div>
						<div
							className="absolute -left-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border border-white/80 text-[9px] font-black leading-none text-slate-900 shadow-[0_8px_18px_rgba(15,23,42,0.18)] ring-1 ring-black/5 dark:border-white/20 dark:ring-white/10"
							style={{
								background: `linear-gradient(135deg, ${medal.colors[0]} 0%, ${medal.colors[1]} 100%)`,
							}}
							aria-label={medal.badge}
						>
							<span className="absolute inset-[1px] rounded-full bg-[radial-gradient(circle_at_30%_28%,rgba(255,255,255,0.72),transparent_42%)]" />
							<span className="relative z-10">{rank + 1}</span>
						</div>
					</div>
					<div className="min-w-0 flex-1">
					<div className="flex items-center gap-2">
						<p className="truncate text-sm font-bold text-light-text dark:text-dark-text">
							{holding.token.ticker}
						</p>
					</div>
					<p className="mt-1 truncate text-xs text-light-accent dark:text-dark-accent">
						{holding.token.name}
					</p>
					<p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
						{formatTokenPrice(holding.token)}
					</p>
				</div>
				<div className="min-w-[92px] shrink-0 text-right">
					<div className="rounded-xl bg-light-surface px-3 py-2 dark:bg-dark-surface">
						<div className="flex items-baseline justify-end gap-1.5">
							<p className="text-[10px] uppercase tracking-wide text-light-accent dark:text-dark-accent">
								{valuationLabel}
							</p>
							<p
								className="font-semibold text-light-text dark:text-dark-text"
								title={formatHoldingUsdValueFull(holding, btcPrice)}
							>
								{formatHoldingUsdValue(holding, btcPrice)}
							</p>
						</div>
					</div>
					<div className="mt-2 rounded-xl bg-light-surface px-3 py-2 dark:bg-dark-surface">
						<div className="flex items-baseline justify-end gap-1.5">
							<p className="text-[10px] uppercase tracking-wide text-light-accent dark:text-dark-accent">
								{holdingsLabel}
							</p>
							<p
								className="font-semibold text-light-text dark:text-dark-text"
								title={formatHoldingBalanceFull(holding)}
							>
								{formatHoldingBalanceCompact(holding)}
							</p>
						</div>
					</div>
					{rank < 10 ? (
						<p
							className={cn(
								"mt-2 text-[11px] font-medium",
								getSupportAgeToneClass(holding.supportAgeDays),
							)}
						>
							{formatSupportAgeLabel(holding.supportAgeDays, language)}
						</p>
					) : null}
				</div>
			</div>
		</div>
	);
}

function FoundationAccountCard({
	copyLabel,
	idLabel,
	language,
	onCopyPrincipal,
	isCopied,
}: FoundationAccountCardProps) {
	const { userInfo, isLoading } = useUserInfo({
		principal: FOUNDATION_PRINCIPAL,
		enabled: true,
	});

	const specialInfo = getSpecialAddressInfo(FOUNDATION_PRINCIPAL, language);
	const displayName =
		specialInfo?.displayName ||
		userInfo?.username ||
		(language === "zh" ? "BabyOdin DAO" : "BabyOdin DAO");
	const avatarUrl = userInfo?.image
		? `https://api.odin.fun/v1/user/${FOUNDATION_PRINCIPAL}/image`
		: `https://api.odin.fun/v1/user/${FOUNDATION_PRINCIPAL}/image`;
	const subtitle = useMemo(() => {
		if (specialInfo?.tag?.label) {
			return specialInfo.tag.label;
		}
		return language === "zh" ? "DAO账号" : "DAO Account";
	}, [language, specialInfo?.tag?.label]);

	return (
		<div className="rounded-[24px] border border-light-border/80 bg-light-background/80 p-4 shadow-sm dark:border-dark-border/80 dark:bg-dark-background/70">
			<div className="flex items-start gap-3">
				<div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-full border border-white/70 bg-light-surface shadow-sm dark:bg-dark-surface">
					<Image
						src={avatarUrl}
						alt={displayName}
						fill
						sizes="56px"
						className="object-cover"
					/>
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex min-w-0 items-center gap-2">
						<p className="truncate text-sm font-bold text-light-text dark:text-dark-text">
							{isLoading ? (language === "zh" ? "加载中..." : "Loading...") : displayName}
						</p>
						<span className="rounded-full bg-light-primary/10 px-2 py-0.5 text-[10px] font-semibold text-light-primary dark:bg-dark-primary/15 dark:text-dark-primary">
							{subtitle}
						</span>
					</div>
					<div className="mt-3 rounded-2xl bg-light-surface/90 px-3 py-2.5 dark:bg-dark-surface/90">
						<div className="flex items-start justify-between gap-3">
							<div className="min-w-0 flex-1">
								<p className="text-[11px] font-medium uppercase tracking-[0.18em] text-light-accent dark:text-dark-accent">
									{idLabel}
								</p>
								<p className="mt-1 break-all font-mono text-[12px] leading-5 text-light-text dark:text-dark-text">
									{FOUNDATION_PRINCIPAL}
								</p>
							</div>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={() => void onCopyPrincipal()}
								className="mt-0.5 h-9 w-9 shrink-0 rounded-full border border-light-border/70 bg-white/80 text-light-text hover:bg-white dark:border-dark-border/70 dark:bg-dark-surface dark:text-dark-text dark:hover:bg-dark-background"
								aria-label={copyLabel}
							>
								{isCopied ? (
									<Check className="h-4 w-4 text-green-500" />
								) : (
									<Copy className="h-4 w-4" />
								)}
							</Button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

interface PromoContentProps {
	holdings: SponsoredHolding[];
	isMobile: boolean;
	onClose: () => void;
	btcPrice: number;
}

function PromoContent({ holdings, isMobile, onClose, btcPrice }: PromoContentProps) {
	const { language, getTranslation } = useLanguage();
	const router = useRouter();
	const [activeTab, setActiveTab] = useState("ranking");
	const [copiedPrincipal, setCopiedPrincipal] = useState(false);
	const [copiedQq, setCopiedQq] = useState(false);
	const closeTimeoutRef = useRef<number | null>(null);
	const copiedPrincipalTimeoutRef = useRef<number | null>(null);
	const copiedQqTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		if (!holdings.length) {
			setActiveTab("rules");
			return;
		}
		setActiveTab("ranking");
	}, [holdings.length]);

	useEffect(() => {
		return () => {
			if (closeTimeoutRef.current) {
				window.clearTimeout(closeTimeoutRef.current);
			}
			if (copiedPrincipalTimeoutRef.current) {
				window.clearTimeout(copiedPrincipalTimeoutRef.current);
			}
			if (copiedQqTimeoutRef.current) {
				window.clearTimeout(copiedQqTimeoutRef.current);
			}
		};
	}, []);

	const handleCopyAndClose = async () => {
		try {
			await navigator.clipboard.writeText(FOUNDATION_PRINCIPAL);
			setCopiedPrincipal(true);
			if (copiedPrincipalTimeoutRef.current) {
				window.clearTimeout(copiedPrincipalTimeoutRef.current);
			}
			copiedPrincipalTimeoutRef.current = window.setTimeout(() => {
				setCopiedPrincipal(false);
				copiedPrincipalTimeoutRef.current = null;
			}, 2000);
			toast.success(getTranslation("plaza.copySuccess"), {
				duration: 2000,
			});
			if (closeTimeoutRef.current) {
				window.clearTimeout(closeTimeoutRef.current);
			}
			closeTimeoutRef.current = window.setTimeout(() => {
				onClose();
				closeTimeoutRef.current = null;
			}, 2000);
		} catch {
			toast.error(getTranslation("plaza.copyFailed"));
		}
	};

	const handleBoostNow = () => {
		onClose();
		const query = new URLSearchParams({
			recipient: FOUNDATION_PRINCIPAL,
			preset: FOUNDATION_PRESET,
		});
		router.push(`/tools/single-transfer?${query.toString()}`);
	};

	const handleCopySponsoredQq = async () => {
		try {
			await navigator.clipboard.writeText(SPONSORED_COPY_QQ);
			setCopiedQq(true);
			if (copiedQqTimeoutRef.current) {
				window.clearTimeout(copiedQqTimeoutRef.current);
			}
			copiedQqTimeoutRef.current = window.setTimeout(() => {
				setCopiedQq(false);
				copiedQqTimeoutRef.current = null;
			}, 2000);
			toast.success(
				language === "zh"
					? "QQ号已复制"
					: "QQ number copied",
				{ duration: 2000 },
			);
		} catch {
			toast.error(
				language === "zh"
					? "复制QQ号失败"
					: "Failed to copy QQ number",
			);
		}
	};

	return (
		<div
			className={cn(
				"flex min-h-0 flex-col overflow-hidden rounded-[28px] bg-gradient-to-br from-light-primary/12 via-light-surface to-light-accent/10 dark:from-dark-primary/18 dark:via-dark-surface dark:to-dark-accent/15",
				isMobile ? "h-full" : "max-h-[78vh]",
			)}
		>
			<div className={cn("border-b border-light-border/70 px-4 pb-4 pt-5 dark:border-dark-border/70", !isMobile && "px-6 pt-6")}>
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0">
						<p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-light-accent dark:text-dark-accent">
							{getTranslation("plaza.promoDialogEyebrow")}
						</p>
						<h2 className={cn("mt-2 font-extrabold text-light-text dark:text-dark-text", isMobile ? "text-xl" : "text-[28px] leading-none")}>
							{getTranslation("plaza.promoMechanismTitle")}
						</h2>
					</div>
					<div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-light-primary/12 px-3 py-1.5 text-xs font-semibold text-light-primary shadow-sm dark:bg-dark-primary/18 dark:text-dark-primary">
						<Sparkles className="h-3.5 w-3.5" />
						{getTranslation("plaza.supportRanking")}
					</div>
				</div>
			</div>

			<div className={cn("flex min-h-0 flex-1 flex-col px-4 pt-4", !isMobile && "px-6")}>
				<Tabs
					value={activeTab}
					onValueChange={setActiveTab}
					activationMode="automatic"
					className="flex min-h-0 flex-1 flex-col"
				>
					<TabsList className="grid h-auto w-full grid-cols-2 rounded-full border border-light-border/70 bg-white/70 p-1 dark:border-dark-border/70 dark:bg-dark-background/70">
						<TabsTrigger
							value="ranking"
							className="rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-light-primary data-[state=active]:text-white data-[state=active]:shadow-none dark:data-[state=active]:bg-dark-primary"
						>
							{getTranslation("plaza.promoRankingTab")}
						</TabsTrigger>
						<TabsTrigger
							value="rules"
							className="rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-light-primary data-[state=active]:text-white data-[state=active]:shadow-none dark:data-[state=active]:bg-dark-primary"
						>
							{getTranslation("plaza.promoRulesTab")}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="ranking" className="mt-4 min-h-0 flex-1 overflow-hidden focus-visible:outline-none">
						<div className="flex h-full min-h-0 flex-col rounded-[24px] border border-light-border/70 bg-light-surface/90 p-3 dark:border-dark-border/70 dark:bg-dark-background/65">
							<div
								data-vaul-no-drag
								className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 touch-pan-y [scrollbar-color:rgba(76,175,80,0.45)_transparent] [scrollbar-width:thin]"
							>
								<div className={cn("space-y-3", isMobile && "space-y-2.5")}>
									{holdings.length > 0 ? (
										holdings.map((holding, index) =>
											isMobile ? (
												<MobileRankingItem
													key={holding.token.id}
													holding={holding}
													rank={index}
													holdingsLabel={getTranslation("plaza.holders")}
													valuationLabel={getTranslation("plaza.valuation")}
													btcPrice={btcPrice}
												/>
											) : (
												<RankingItem
													key={holding.token.id}
													holding={holding}
													rank={index}
													active={index === 0}
													onClick={() => undefined}
													btcPrice={btcPrice}
													showSupportAge={index < 10}
												/>
											),
										)
									) : (
										<div className="flex h-full min-h-[220px] items-center justify-center rounded-[22px] border border-dashed border-light-border/80 bg-light-background/70 px-6 text-center text-sm text-gray-500 dark:border-dark-border/80 dark:bg-dark-background/60 dark:text-gray-400">
											{getTranslation("plaza.promoRankingEmpty")}
										</div>
									)}
								</div>
							</div>
						</div>
					</TabsContent>

					<TabsContent
						value="rules"
						data-vaul-no-drag
						className="mt-4 min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1 touch-pan-y focus-visible:outline-none [scrollbar-color:rgba(76,175,80,0.45)_transparent] [scrollbar-width:thin]"
					>
						<div className="rounded-[24px] border border-light-border/70 bg-light-surface/90 p-4 dark:border-dark-border/70 dark:bg-dark-background/65">
							<p className="text-base font-semibold text-light-text dark:text-dark-text">
								{getTranslation("plaza.promoRulesTitle")}
							</p>
							<div className="mt-3 rounded-2xl bg-light-background/90 px-3.5 py-3 text-[13px] leading-6 text-gray-600 dark:bg-dark-background/90 dark:text-gray-300">
								<p>
									BabyOdin {language === "zh" ? "DAO账号中，按" : "foundation account:"}
									<span className="font-semibold text-light-text dark:text-dark-text">
										{language === "zh"
											? " 持仓价值排名前 3 的社区代币将获得展示资格。"
											: " the top 3 community tokens ranked by holding value will receive display eligibility."}
									</span>
								</p>
								<p className="mt-1.5">
									{getTranslation("plaza.promoMechanismDescriptionLine2")}
								</p>
								<div className="mt-3">
									<p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-semibold leading-6 text-amber-600 dark:text-amber-300">
										<span>
											{language === "zh"
												? "自定义广告词请联系QQ"
												: "For custom ad copy, contact QQ"}
										</span>
										<span className="font-black tracking-[0.04em]">
											{SPONSORED_COPY_QQ}
										</span>
										<button
											type="button"
											onClick={() => void handleCopySponsoredQq()}
											className="inline-flex h-7 w-7 items-center justify-center rounded-full text-amber-600 transition hover:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-300/10"
											aria-label={
												language === "zh"
													? "复制QQ号"
													: "Copy QQ number"
											}
										>
											{copiedQq ? (
												<Check className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
											) : (
												<Copy className="h-3.5 w-3.5" />
											)}
										</button>
									</p>
								</div>
							</div>
						</div>

						<div className="rounded-[24px] border border-light-border/70 bg-light-surface/90 p-4 dark:border-dark-border/70 dark:bg-dark-background/65">
							<FoundationAccountCard
								copyLabel={getTranslation("plaza.copyLabel")}
								idLabel={getTranslation("plaza.identityId")}
								language={language}
								onCopyPrincipal={handleCopyAndClose}
								isCopied={copiedPrincipal}
							/>
						</div>
					</TabsContent>
				</Tabs>
			</div>

			<div className={cn("border-t border-light-border/70 bg-white/70 px-4 py-4 backdrop-blur-sm dark:border-dark-border/70 dark:bg-dark-background/40", !isMobile && "px-6")}>
				<div className="grid grid-cols-2 gap-3">
					<Button
						type="button"
						variant="outline"
						onClick={() => void handleCopyAndClose()}
						className="h-11 rounded-2xl border-light-border bg-light-surface/90 text-light-text hover:bg-light-background dark:border-dark-border dark:bg-dark-surface/90 dark:text-dark-text dark:hover:bg-dark-background"
					>
						<Copy className="h-4 w-4" />
						{getTranslation("plaza.copyFoundationAddress")}
					</Button>
					<Button
						type="button"
						onClick={handleBoostNow}
						className="h-11 rounded-2xl bg-light-primary text-white shadow-[0_12px_32px_rgba(76,175,80,0.28)] hover:bg-light-primary/90 dark:bg-dark-primary dark:hover:bg-dark-primary/90"
					>
						<Sparkles className="h-4 w-4" />
						{getTranslation("plaza.boostNow")}
					</Button>
				</div>
			</div>
		</div>
	);
}

export function PromoDialog({
	open,
	onOpenChange,
	holdings,
	btcPrice,
}: PromoDialogProps) {
	const { getTranslation } = useLanguage();
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const media = window.matchMedia("(max-width: 1023px)");
		const update = () => setIsMobile(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	const handleClose = () => onOpenChange(false);

		if (isMobile) {
			return (
				<Drawer open={open} onOpenChange={onOpenChange}>
					<DrawerContent className="flex h-[90dvh] max-h-[90dvh] flex-col overflow-hidden rounded-t-[28px] border-light-border bg-light-surface p-0 dark:border-dark-border dark:bg-dark-surface">
						<DrawerHeader className="sr-only">
						<DrawerTitle>{getTranslation("plaza.supportRanking")}</DrawerTitle>
						<DrawerDescription>
							{getTranslation("plaza.promoMechanismTitle")}
						</DrawerDescription>
					</DrawerHeader>
					<PromoContent holdings={holdings} isMobile={true} onClose={handleClose} btcPrice={btcPrice} />
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl overflow-hidden rounded-[28px] border-light-border bg-light-surface p-0 shadow-2xl dark:border-dark-border dark:bg-dark-surface">
				<DialogHeader className="sr-only">
					<DialogTitle>{getTranslation("plaza.supportRanking")}</DialogTitle>
					<DialogDescription>
						{getTranslation("plaza.promoMechanismTitle")}
					</DialogDescription>
				</DialogHeader>
				<PromoContent holdings={holdings} isMobile={false} onClose={handleClose} btcPrice={btcPrice} />
			</DialogContent>
		</Dialog>
	);
}
